/**
 * Nachrichtencenter (P2.7 Tenant-Admin-Center) — Plattform-Nachrichten an
 * Mandanten (Wartung, Stoerung, Aenderung, Information).
 *
 * Operatoren legen hier Nachrichten an, die im Tenant-Admin-Portal der
 * Zielgruppe (alle / Plaene / einzelne Mandanten) erscheinen. Aktive
 * Wartungs-/Stoerungsnachrichten fliessen zusaetzlich in den Dienststatus
 * der Mandanten ein.
 *
 * Muster wie ContextsPage: Tabelle + refresh() + Inline-Formular + axios CRUD
 * + Fehler-Banner. Read-only-Operatoren (platform-readonly, operator.readOnly
 * aus useAuth()) sehen nur die Liste, ohne Anlegen/Bearbeiten/Loeschen.
 *
 * Der Nachrichtentext ist Klartext: er wird ausschliesslich als React-Text
 * (whitespace-pre-wrap) gerendert, nie als HTML.
 *
 * Backend: GET/POST /api/platform-messages, PUT/DELETE /api/platform-messages/:id.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import api, { extractApiError, type TenantSummary } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  type MessageCategory,
  type MessageFormState,
  type MessageSeverity,
  type PlatformMessage,
  MESSAGE_CATEGORIES,
  MESSAGE_SEVERITIES,
  PLAN_OPTIONS,
  PUBLICATION_STATUS_LABEL,
  SERVICE_OPTIONS,
  audienceSummary,
  createPlatformMessage,
  deletePlatformMessage,
  emptyMessageForm,
  formToPayload,
  formatDateTime,
  labelForCategory,
  labelForService,
  labelForSeverity,
  listPlatformMessages,
  messageToForm,
  parseSlugList,
  publicationStatus,
  readCountLabel,
  toggleSlugInList,
  updatePlatformMessage,
  validateMessageForm,
} from '../services/platform-messages';

const CATEGORY_COLORS: Record<MessageCategory, string> = {
  maintenance: 'bg-blue-100 text-blue-800',
  incident: 'bg-red-100 text-red-700',
  change: 'bg-purple-100 text-purple-800',
  info: 'bg-slate-100 text-slate-700',
};

const SEVERITY_COLORS: Record<MessageSeverity, string> = {
  info: 'bg-slate-100 text-slate-700',
  warning: 'bg-amber-100 text-amber-800',
  critical: 'bg-red-100 text-red-700',
};

const STATUS_COLORS: Record<string, string> = {
  scheduled: 'bg-blue-100 text-blue-800',
  active: 'bg-green-100 text-green-800',
  expired: 'bg-slate-200 text-slate-600',
};

/** Formular-Modus: geschlossen, neu anlegen oder bestehende Nachricht bearbeiten. */
type FormMode = { kind: 'closed' } | { kind: 'create' } | { kind: 'edit'; message: PlatformMessage };

export function PlatformMessagesPage() {
  const { operator } = useAuth();
  const isReadOnly = !!operator?.readOnly;
  const [rows, setRows] = useState<PlatformMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<'' | MessageCategory>('');
  const [mode, setMode] = useState<FormMode>({ kind: 'closed' });
  const [expanded, setExpanded] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  function refresh(): void {
    setLoading(true);
    listPlatformMessages()
      .then((items) => setRows(items))
      .catch((err) => setError(extractApiError(err)))
      .finally(() => setLoading(false));
  }

  useEffect(() => { refresh(); }, []);

  const visibleRows = useMemo(
    () => (categoryFilter ? rows.filter((m) => m.category === categoryFilter) : rows),
    [rows, categoryFilter],
  );

  async function remove(msg: PlatformMessage): Promise<void> {
    if (!window.confirm(
      `Nachricht "${msg.title}" löschen? Sie verschwindet sofort bei allen Mandanten; `
      + 'der Lesestatus geht verloren.',
    )) return;
    setBusyId(msg.id);
    setError(null);
    try {
      await deletePlatformMessage(msg.id);
      if (mode.kind === 'edit' && mode.message.id === msg.id) setMode({ kind: 'closed' });
      refresh();
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setBusyId(null);
    }
  }

  const colCount = isReadOnly ? 7 : 8;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-4">
        <div>
          <h2 className="text-xl font-semibold">Nachrichtencenter</h2>
          <p className="text-sm text-slate-500">
            Plattform-Nachrichten an Mandanten (Wartung, Störung, Änderung,
            Information). Sie erscheinen im Tenant-Admin-Portal der gewählten
            Zielgruppe; aktive Wartungen und Störungen fließen in den Dienststatus ein.
          </p>
        </div>
        {!isReadOnly && (
          <button
            onClick={() => setMode((m) => (m.kind === 'create' ? { kind: 'closed' } : { kind: 'create' }))}
            className="btn-primary whitespace-nowrap"
          >
            {mode.kind === 'create' ? 'Schliessen' : 'Nachricht anlegen'}
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded p-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {!isReadOnly && mode.kind !== 'closed' && (
        <MessageForm
          key={mode.kind === 'edit' ? mode.message.id : 'new'}
          message={mode.kind === 'edit' ? mode.message : null}
          onClose={() => setMode({ kind: 'closed' })}
          onSaved={() => { setMode({ kind: 'closed' }); setError(null); refresh(); }}
        />
      )}

      <div className="flex items-center gap-2 mb-3 text-sm">
        <label htmlFor="pm-category-filter" className="text-slate-600">Kategorie:</label>
        <select
          id="pm-category-filter"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value as '' | MessageCategory)}
          className="px-2 py-1 rounded border text-sm"
        >
          <option value="">Alle</option>
          {MESSAGE_CATEGORIES.map((c) => (
            <option key={c} value={c}>{labelForCategory(c)}</option>
          ))}
        </select>
        <span className="text-slate-500">
          {visibleRows.length} von {rows.length} Nachrichten
        </span>
      </div>

      {loading ? <p className="text-slate-500">Lade…</p> : (
        <table className="w-full bg-white shadow rounded">
          <thead className="bg-slate-100 text-left text-sm uppercase tracking-wide">
            <tr>
              <th className="p-3">Kategorie</th>
              <th className="p-3">Titel</th>
              <th className="p-3">Zielgruppe</th>
              <th className="p-3">Veröffentlicht</th>
              <th className="p-3">Läuft ab</th>
              <th className="p-3">Status</th>
              <th className="p-3">Gelesen</th>
              {!isReadOnly && <th className="p-3">Aktionen</th>}
            </tr>
          </thead>
          <tbody className="text-sm">
            {visibleRows.map((msg) => {
              const status = publicationStatus(msg);
              const isOpen = expanded === msg.id;
              return (
                <MessageRows
                  key={msg.id}
                  msg={msg}
                  status={status}
                  isOpen={isOpen}
                  colCount={colCount}
                  isReadOnly={isReadOnly}
                  busy={busyId === msg.id}
                  onToggle={() => setExpanded(isOpen ? null : msg.id)}
                  onEdit={() => setMode({ kind: 'edit', message: msg })}
                  onDelete={() => remove(msg)}
                />
              );
            })}
            {visibleRows.length === 0 && (
              <tr><td colSpan={colCount} className="p-6 text-center text-slate-500">
                {rows.length === 0
                  ? 'Noch keine Plattform-Nachrichten angelegt.'
                  : 'Keine Nachrichten in dieser Kategorie.'}
              </td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

interface MessageRowsProps {
  msg: PlatformMessage;
  status: ReturnType<typeof publicationStatus>;
  isOpen: boolean;
  colCount: number;
  isReadOnly: boolean;
  busy: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

/** Tabellenzeile plus aufklappbare Detailzeile (Text, Dienste, Zeitraum). */
function MessageRows({
  msg, status, isOpen, colCount, isReadOnly, busy, onToggle, onEdit, onDelete,
}: MessageRowsProps) {
  return (
    <>
      <tr className="border-t hover:bg-slate-50 align-top">
        <td className="p-3">
          <div className="flex flex-col items-start gap-1">
            <span className={`badge ${CATEGORY_COLORS[msg.category] ?? 'bg-slate-100 text-slate-500'}`}>
              {labelForCategory(msg.category)}
            </span>
            <span className={`badge ${SEVERITY_COLORS[msg.severity] ?? 'bg-slate-100 text-slate-500'}`}>
              {labelForSeverity(msg.severity)}
            </span>
          </div>
        </td>
        <td className="p-3">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={isOpen}
            className="text-left font-medium hover:underline"
          >
            {msg.title}
          </button>
          {msg.actionRequired && (
            <span className="badge bg-amber-100 text-amber-800 ml-2">Handlung erforderlich</span>
          )}
        </td>
        <td className="p-3 text-slate-600">{audienceSummary(msg)}</td>
        <td className="p-3 text-slate-500" title={msg.publishedAt}>{formatDateTime(msg.publishedAt)}</td>
        <td className="p-3 text-slate-500" title={msg.expiresAt ?? undefined}>{formatDateTime(msg.expiresAt)}</td>
        <td className="p-3">
          <span className={`badge ${STATUS_COLORS[status]}`}>{PUBLICATION_STATUS_LABEL[status]}</span>
        </td>
        <td className="p-3 text-slate-600 whitespace-nowrap">{readCountLabel(msg.readCount)}</td>
        {!isReadOnly && (
          <td className="p-3">
            <div className="flex items-center gap-3">
              <button
                onClick={onEdit}
                disabled={busy}
                className="text-slate-600 hover:underline disabled:text-slate-400 disabled:no-underline"
              >
                Bearbeiten
              </button>
              <button
                onClick={onDelete}
                disabled={busy}
                className="text-red-600 hover:underline disabled:text-slate-400 disabled:no-underline"
              >
                Löschen
              </button>
            </div>
          </td>
        )}
      </tr>
      {isOpen && (
        <tr className="bg-slate-50">
          <td colSpan={colCount} className="p-4 text-sm">
            {/* Klartext: React escaped den Inhalt, Zeilenumbrueche bleiben erhalten. */}
            <p className="whitespace-pre-wrap break-words mb-3">{msg.body}</p>
            <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-slate-600">
              <dt className="font-medium">Betroffene Dienste</dt>
              <dd>
                {msg.affectedServices?.length
                  ? msg.affectedServices.map(labelForService).join(', ')
                  : 'Allgemein (kein bestimmter Dienst)'}
              </dd>
              <dt className="font-medium">Zeitraum</dt>
              <dd>
                {msg.startsAt || msg.endsAt
                  ? `${formatDateTime(msg.startsAt)} – ${formatDateTime(msg.endsAt)}`
                  : '—'}
              </dd>
              <dt className="font-medium">Zielgruppe</dt>
              <dd>{audienceSummary(msg, Number.POSITIVE_INFINITY)}</dd>
              <dt className="font-medium">Angelegt</dt>
              <dd>{formatDateTime(msg.createdAt)}{msg.createdBy ? ` von ${msg.createdBy}` : ''}</dd>
              <dt className="font-medium">Geändert</dt>
              <dd>{formatDateTime(msg.updatedAt)}</dd>
            </dl>
          </td>
        </tr>
      )}
    </>
  );
}

interface MessageFormProps {
  /** null = neu anlegen. */
  message: PlatformMessage | null;
  onClose: () => void;
  onSaved: () => void;
}

function MessageForm({ message, onClose, onSaved }: MessageFormProps) {
  const [form, setForm] = useState<MessageFormState>(() => (message ? messageToForm(message) : emptyMessageForm()));
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [tenants, setTenants] = useState<TenantSummary[] | null>(null);
  const [tenantQuery, setTenantQuery] = useState('');

  // Mandantenliste fuer die Auswahl (bestehende /tenants-API, max. 200 je Seite).
  // Fehler sind unkritisch: dann bleibt nur die freie Slug-Eingabe.
  useEffect(() => {
    if (form.audience !== 'tenants' || tenants !== null) return;
    api.get<{ rows: TenantSummary[] }>('/tenants', { params: { limit: 200 } })
      .then((r) => setTenants(r.data.rows ?? []))
      .catch(() => setTenants([]));
  }, [form.audience, tenants]);

  function set<K extends keyof MessageFormState>(key: K, value: MessageFormState[K]): void {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function toggleIn(key: 'affectedServices' | 'audiencePlans', id: string): void {
    setForm((f) => {
      const list = f[key];
      return { ...f, [key]: list.includes(id) ? list.filter((x) => x !== id) : [...list, id] };
    });
  }

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const problems = validateMessageForm(form);
    setErrors(problems);
    if (problems.length > 0) return;
    setBusy(true);
    try {
      const payload = formToPayload(form);
      if (message) await updatePlatformMessage(message.id, payload);
      else await createPlatformMessage(payload);
      onSaved();
    } catch (err) {
      setErrors([extractApiError(err)]);
    } finally {
      setBusy(false);
    }
  }

  const selectedSlugs = parseSlugList(form.audienceTenants);
  const q = tenantQuery.trim().toLowerCase();
  const tenantChoices = (tenants ?? []).filter(
    (t) => !q || t.slug.includes(q) || t.name.toLowerCase().includes(q),
  );

  const inputCls = 'w-full px-2 py-1 rounded border text-sm';

  return (
    <form onSubmit={submit} className="bg-white shadow rounded p-4 mb-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">{message ? 'Nachricht bearbeiten' : 'Nachricht anlegen'}</h3>
        <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-700" aria-label="Schliessen">×</button>
      </div>

      {errors.length > 0 && (
        <ul className="bg-red-50 border border-red-200 text-red-800 rounded p-3 text-sm list-disc pl-6">
          {errors.map((e) => <li key={e}>{e}</li>)}
        </ul>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="pm-category" className="block text-sm font-medium mb-1">Kategorie</label>
          <select
            id="pm-category"
            value={form.category}
            onChange={(e) => set('category', e.target.value as MessageCategory)}
            className={inputCls}
          >
            {MESSAGE_CATEGORIES.map((c) => <option key={c} value={c}>{labelForCategory(c)}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="pm-severity" className="block text-sm font-medium mb-1">Schweregrad</label>
          <select
            id="pm-severity"
            value={form.severity}
            onChange={(e) => set('severity', e.target.value as MessageSeverity)}
            className={inputCls}
          >
            {MESSAGE_SEVERITIES.map((s) => <option key={s} value={s}>{labelForSeverity(s)}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label htmlFor="pm-title" className="block text-sm font-medium mb-1">Titel</label>
        <input
          id="pm-title"
          value={form.title}
          onChange={(e) => set('title', e.target.value)}
          maxLength={200}
          placeholder="z.B. Wartung E-Mail-Infrastruktur"
          className={inputCls}
          required
        />
      </div>

      <div>
        <label htmlFor="pm-body" className="block text-sm font-medium mb-1">Text (Klartext, kein HTML)</label>
        <textarea
          id="pm-body"
          value={form.body}
          onChange={(e) => set('body', e.target.value)}
          rows={6}
          className={inputCls}
          required
        />
      </div>

      <fieldset>
        <legend className="block text-sm font-medium mb-1">Betroffene Dienste (leer = allgemein)</legend>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
          {SERVICE_OPTIONS.map((s) => (
            <label key={s.id} className="inline-flex items-center gap-1">
              <input
                type="checkbox"
                checked={form.affectedServices.includes(s.id)}
                onChange={() => toggleIn('affectedServices', s.id)}
              />
              {s.label}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="pm-starts" className="block text-sm font-medium mb-1">Beginn (Wartungsfenster / Störung)</label>
          <input
            id="pm-starts"
            type="datetime-local"
            value={form.startsAt}
            onChange={(e) => set('startsAt', e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="pm-ends" className="block text-sm font-medium mb-1">Ende</label>
          <input
            id="pm-ends"
            type="datetime-local"
            value={form.endsAt}
            onChange={(e) => set('endsAt', e.target.value)}
            className={inputCls}
          />
        </div>
      </div>

      <label className="inline-flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.actionRequired}
          onChange={(e) => set('actionRequired', e.target.checked)}
        />
        Handlung durch Mandanten-Admin erforderlich
      </label>

      <fieldset className="space-y-2">
        <legend className="block text-sm font-medium mb-1">Zielgruppe</legend>
        <div className="flex flex-wrap gap-4 text-sm">
          {([
            ['all', 'Alle Mandanten'],
            ['plans', 'Nach Plan'],
            ['tenants', 'Einzelne Mandanten'],
          ] as const).map(([value, label]) => (
            <label key={value} className="inline-flex items-center gap-1">
              <input
                type="radio"
                name="pm-audience"
                value={value}
                checked={form.audience === value}
                onChange={() => set('audience', value)}
              />
              {label}
            </label>
          ))}
        </div>

        {form.audience === 'plans' && (
          <div className="flex flex-wrap gap-4 text-sm pl-1">
            {PLAN_OPTIONS.map((p) => (
              <label key={p.id} className="inline-flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={form.audiencePlans.includes(p.id)}
                  onChange={() => toggleIn('audiencePlans', p.id)}
                />
                {p.label}
              </label>
            ))}
          </div>
        )}

        {form.audience === 'tenants' && (
          <div className="space-y-2">
            <div>
              <label htmlFor="pm-tenants" className="block text-xs text-slate-600 mb-1">
                Mandanten-Slugs (kommagetrennt)
              </label>
              <input
                id="pm-tenants"
                value={form.audienceTenants}
                onChange={(e) => set('audienceTenants', e.target.value)}
                placeholder="acme, globex"
                className={`${inputCls} font-mono`}
              />
            </div>
            {tenants === null && <p className="text-xs text-slate-500">Lade Mandanten…</p>}
            {tenants !== null && tenants.length > 0 && (
              <div className="border rounded p-2">
                <input
                  value={tenantQuery}
                  onChange={(e) => setTenantQuery(e.target.value)}
                  placeholder="Mandanten filtern…"
                  aria-label="Mandanten filtern"
                  className="w-full px-2 py-1 rounded border text-xs mb-2"
                />
                <div className="max-h-40 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-1 text-sm">
                  {tenantChoices.map((t) => (
                    <label key={t.slug} className="inline-flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={selectedSlugs.includes(t.slug)}
                        onChange={() => set('audienceTenants', toggleSlugInList(form.audienceTenants, t.slug))}
                      />
                      <span className="font-mono">{t.slug}</span>
                      <span className="text-slate-500 truncate">{t.name}</span>
                    </label>
                  ))}
                  {tenantChoices.length === 0 && (
                    <span className="text-xs text-slate-500">Keine Treffer.</span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </fieldset>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="pm-published" className="block text-sm font-medium mb-1">Veröffentlichen ab</label>
          <input
            id="pm-published"
            type="datetime-local"
            value={form.publishedAt}
            onChange={(e) => set('publishedAt', e.target.value)}
            className={inputCls}
          />
          <p className="text-xs text-slate-500 mt-1">Zeitpunkt in der Zukunft = geplante Veröffentlichung.</p>
        </div>
        <div>
          <label htmlFor="pm-expires" className="block text-sm font-medium mb-1">Läuft ab (optional)</label>
          <input
            id="pm-expires"
            type="datetime-local"
            value={form.expiresAt}
            onChange={(e) => set('expiresAt', e.target.value)}
            className={inputCls}
          />
        </div>
      </div>

      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onClose} className="px-3 py-1 rounded border" disabled={busy}>
          Abbrechen
        </button>
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? 'Speichere…' : 'Speichern'}
        </button>
      </div>
    </form>
  );
}
